import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Popover } from 'antd';
import { ScissorOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { captureAnalytics } from '../../actions/preload';
import './patientSplit.scss';

export const patientSplitDocumentUrl = (document, fromPage) => {
  const url = `/app/view/${encodeURIComponent(btoa(document.file_token))}?from=${encodeURIComponent(fromPage)}&sendToken=${encodeURIComponent(document.send_token)}`;
  return fromPage === 'assigned' && document.assigned_document_id
    ? `${url}&assignedDocumentId=${encodeURIComponent(document.assigned_document_id)}` : url;
};

const PatientSplitDetails = ({
  split, failed, onNavigate, fromPage,
}) => {
  if (failed || split?.role === 'unknown') return <p role="status">Patient split details are unavailable. Try again shortly.</p>;
  if (!split) return <p role="status">Loading patient split details…</p>;
  if (split.role === 'child') {
    return (
      <>
        <p>This document was split from a multi-patient document.</p>
        {split.original ? (
          <Link to={patientSplitDocumentUrl(split.original, fromPage)} onClick={(event) => onNavigate(event, 'original')}>
            View parent document
          </Link>
        ) : <p className="patient-split-details__muted">The parent document is not accessible.</p>}
      </>
    );
  }
  if (split.handoff === 'undecided') return <p role="status">Separating patient documents…</p>;
  if (split.handoff === 'original_processing') return <p>This document was kept as a single patient document.</p>;
  if (split.handoff === 'original_sftp') return <p>This document was not split. The original was retained.</p>;
  return (
    <>
      <p className="patient-split-details__title">Patients extracted from this document</p>
      {split.children?.length ? (
        <ul className="patient-split-details__patients">
          {split.children.map((child) => (
            <li key={child.file_token}>
              <Link to={patientSplitDocumentUrl(child, fromPage)} onClick={(event) => onNavigate(event, 'child')}>
                {child.patient_name?.trim() || 'Unnamed patient'}
                <span className="patient-split-details__muted">
                  {`Pages ${child.source_pages.join(', ')}${child.archived ? ' · Archived' : ''}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : <p className="patient-split-details__muted">No patient documents are available to view.</p>}
    </>
  );
};

PatientSplitDetails.propTypes = {
  split: PropTypes.object,
  failed: PropTypes.bool.isRequired,
  onNavigate: PropTypes.func.isRequired,
  fromPage: PropTypes.string.isRequired,
};
PatientSplitDetails.defaultProps = { split: null };

const PatientSplitIcon = ({
  split, failed, surface, onVisibleChange, fromPage,
}) => {
  const [visible, setVisible] = useState(false);
  const changeVisible = (next) => { setVisible(next); onVisibleChange(next); };
  const navigate = (event, destination) => {
    event.stopPropagation();
    captureAnalytics('patient_split_document_opened', { destination, surface });
    changeVisible(false);
  };
  return (
    <Popover
      content={<div className="patient-split-details"><PatientSplitDetails split={split} failed={failed} onNavigate={navigate} fromPage={fromPage} /></div>}
      trigger={['hover', 'click']}
      placement="bottomRight"
      visible={visible}
      onVisibleChange={changeVisible}
      mouseLeaveDelay={0.2}
      overlayClassName="patient-split-popover"
      destroyTooltipOnHide
    >
      <button
        type="button"
        className="patient-split-icon"
        aria-label="Patient split details"
        aria-expanded={visible}
        onClick={(event) => {
          event.stopPropagation();
          if (!visible) captureAnalytics('patient_split_details_opened', { surface });
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Escape') changeVisible(false);
        }}
      >
        <ScissorOutlined />
      </button>
    </Popover>
  );
};

PatientSplitIcon.propTypes = {
  split: PropTypes.object,
  failed: PropTypes.bool,
  surface: PropTypes.string.isRequired,
  fromPage: PropTypes.string,
  onVisibleChange: PropTypes.func,
};
PatientSplitIcon.defaultProps = {
  fromPage: 'received', split: null, failed: false, onVisibleChange: () => {},
};
export default PatientSplitIcon;
